import { useState } from 'react';
import { HelpCircle, Info } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '../ui/tooltip';
import { cn } from '../../lib/utils';

interface HelpTooltipProps {
  text: string;
  title?: string;
  icon?: 'question' | 'info';
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

export default function HelpTooltip({
  text,
  title,
  icon = 'question',
  size = 'sm',
  className = '',
}: HelpTooltipProps) {
  const sizeMap = { sm: 16, md: 20, lg: 24 };
  const Icon = icon === 'question' ? HelpCircle : Info;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          className={cn(
            "text-blue-500 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 focus:outline-none focus-ring rounded-full transition-colors inline-flex items-center",
            className
          )}
          aria-label="Help"
        >
          <Icon size={sizeMap[size]} />
        </button>
      </TooltipTrigger>
      <TooltipContent side="right" className="max-w-xs">
        {title && <p className="font-semibold mb-1">{title}</p>}
        <p className="leading-relaxed">{text}</p>
      </TooltipContent>
    </Tooltip>
  );
}

interface HelpPanelProps {
  title: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
}

export function HelpPanel({ title, children, defaultOpen = false }: HelpPanelProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="mb-4">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
      >
        <Info className="h-4 w-4 mr-1" />
        {isOpen ? 'Hide help' : title}
      </button>

      {isOpen && (
        <div className="mt-2 p-3 bg-blue-50 dark:bg-blue-900/30 rounded-lg text-sm text-blue-800 dark:text-blue-200 border border-blue-200 dark:border-blue-800">
          {children}
        </div>
      )}
    </div>
  );
}
